// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import translate from "@insite/client-framework/Translate";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";
import { FeaturedCategoriesApiParameter, loadFeaturedCategoriesApiParameter } from "@tps/App/Services/CategoriesService";
import { ErrorApiDefaultMessage } from "@tps/XDL/constants";

type HandlerType = ApiHandler<
    FeaturedCategoriesApiParameter & HasOnSuccess<CategoryModel[]> & HasOnError<string>,
    CategoryModel[]
>;

export const DispatchBeginLoadFeaturedCategories: HandlerType = props => {
    props.dispatch({
        type: "xFeaturedCategories/BeginLoadFeaturedCategories" as any,
    });
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = props.parameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await loadFeaturedCategoriesApiParameter(props.apiParameter);
    } catch (error) {
        const failedMessage = error.errorMessage || translate(ErrorApiDefaultMessage);
        props.parameter.onError?.(failedMessage);
        props.dispatch({
            type: "xFeaturedCategories/FailedToLoadFeaturedCategories",
            failedMessage,
        } as any);

        return false;
    }
};

export const DispatchCompleteLoadFeaturedCategories: HandlerType = props => {
    props.dispatch({
        type: "xFeaturedCategories/CompleteLoadFeaturedCategories",
        data: props.apiResult,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    /* Execute on success so each instance of the widget
     * will display it corresponding categories
     * not reading from store for other widget instance data
     */
    props.parameter.onSuccess?.(props.apiResult);
};

export const chain = [
    DispatchBeginLoadFeaturedCategories,
    PopulateApiParameter,
    RequestDataFromApi,
    DispatchCompleteLoadFeaturedCategories,
    ExecuteOnSuccessCallback,
];

const loadFeaturedCategories = createHandlerChainRunner(chain, "LoadFeaturedCategories");
export default loadFeaturedCategories;
